import { useState } from "react";
import { AFRICA_COUNTRIES, PRODUCTS } from "@/data/products";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Upload } from "lucide-react";

const CHANNELS = ["Distributors", "Supermarkets", "Hotels", "Restaurants", "Convenience", "Government", "NGOs", "Airlines"];

export default function Distributor() {
  const [form, setForm] = useState({
    company: "", contact_name: "", email: "", phone: "",
    country: "", website: "", annual_sales: "", warehouse_capacity: "",
    import_licence: "", channels: [], products: [], message: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [fileName, setFileName] = useState("");

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleArr = (k, v) => setForm((f) => ({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v] }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/distributor/apply", form);
      toast.success(data.message);
      setSuccess(true);
    } catch {
      toast.error("Please check the form and try again.");
    } finally { setLoading(false); }
  };

  if (success) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-6 py-24" data-testid="distributor-success">
        <div className="max-w-lg text-center space-y-4">
          <div className="w-16 h-16 mx-auto rounded-full bg-gold-400 text-neutral-950 flex items-center justify-center font-display font-black text-2xl">✓</div>
          <h1 className="font-display font-bold text-3xl">Application received</h1>
          <p className="text-muted-foreground">Thank you for your interest in partnering with SBC Ltd. Our export team will contact you within 5 business days.</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="distributor-page">
      <section className="bg-neutral-950 text-white pt-20 pb-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="eyebrow text-gold-400 mb-4">Distributor Registration</div>
          <h1 className="font-display font-bold text-5xl lg:text-6xl tracking-tighter">Partner with SBC.</h1>
          <p className="mt-4 text-neutral-300 max-w-2xl">Tell us about your market, your capacity and the brands you&rsquo;d like to represent. We&rsquo;ll be in touch within 5 business days.</p>
        </div>
      </section>

      <section className="py-16 bg-background">
        <form onSubmit={submit} className="max-w-4xl mx-auto px-6 space-y-6" data-testid="distributor-form">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Company Name" required value={form.company} onChange={(v) => update("company", v)} testId="input-company" />
            <Field label="Primary Contact" required value={form.contact_name} onChange={(v) => update("contact_name", v)} testId="input-contact-name" />
            <Field label="Email" type="email" required value={form.email} onChange={(v) => update("email", v)} testId="input-email" />
            <Field label="Phone" value={form.phone} onChange={(v) => update("phone", v)} testId="input-phone" />
            <div>
              <Label>Country *</Label>
              <select required value={form.country} onChange={(e) => update("country", e.target.value)} className="w-full h-11 rounded-lg border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold-400" data-testid="input-country">
                <option value="">Select country…</option>
                {AFRICA_COUNTRIES.map((c) => <option key={c}>{c}</option>)}
                <option>United Kingdom</option>
                <option>Other</option>
              </select>
            </div>
            <Field label="Website" value={form.website} onChange={(v) => update("website", v)} testId="input-website" />
            <Field label="Annual Sales (USD)" value={form.annual_sales} onChange={(v) => update("annual_sales", v)} testId="input-annual-sales" placeholder="e.g. $2M – $5M" />
            <Field label="Warehouse Capacity" value={form.warehouse_capacity} onChange={(v) => update("warehouse_capacity", v)} testId="input-warehouse" placeholder="e.g. 2,000 sqm" />
            <Field label="Import Licence No." value={form.import_licence} onChange={(v) => update("import_licence", v)} testId="input-licence" />
          </div>

          <div>
            <Label>Channels Served</Label>
            <div className="flex flex-wrap gap-2">
              {CHANNELS.map((c) => (
                <button type="button" key={c} onClick={() => toggleArr("channels", c)} className={`px-4 py-2 rounded-full text-xs font-medium border transition-colors ${form.channels.includes(c) ? "bg-neutral-950 text-white border-neutral-950 dark:bg-white dark:text-neutral-950 dark:border-white" : "border-border hover:border-gold-400"}`} data-testid={`channel-${c.toLowerCase()}`}>
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label>Brands of Interest</Label>
            <div className="flex flex-wrap gap-2">
              {PRODUCTS.map((p) => (
                <button type="button" key={p.slug} onClick={() => toggleArr("products", p.name)} className={`px-4 py-2 rounded-full text-xs font-medium border transition-colors ${form.products.includes(p.name) ? "bg-gold-400 text-neutral-950 border-gold-400" : "border-border hover:border-gold-400"}`} data-testid={`interest-${p.slug}`}>
                  {p.name}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label>Message</Label>
            <textarea rows={5} value={form.message} onChange={(e) => update("message", e.target.value)} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold-400" placeholder="Tell us about your business and goals with SBC…" data-testid="input-message" />
          </div>

          <div>
            <Label>Upload Documents (optional)</Label>
            <label className="flex items-center gap-3 p-4 rounded-lg border border-dashed border-border cursor-pointer hover:border-gold-400 transition-colors">
              <Upload className="w-4 h-4" />
              <span className="text-sm">{fileName || "Company profile, import licence, references (PDF/DOC/JPG)"}</span>
              <input type="file" className="hidden" onChange={(e) => setFileName(e.target.files?.[0]?.name || "")} data-testid="input-documents" />
            </label>
            <p className="text-xs text-muted-foreground mt-2">Document contents are captured on submission review. You can also email additional files to help@sbcltd.com.</p>
          </div>

          <button type="submit" disabled={loading} className="w-full bg-neutral-950 text-white hover:bg-neutral-800 dark:bg-white dark:text-neutral-950 dark:hover:bg-neutral-100 rounded-full h-12 font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-50" data-testid="submit-distributor">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Submit Application
          </button>
        </form>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, placeholder, testId }) {
  return (
    <div>
      <Label>{label}{required && " *"}</Label>
      <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full h-11 rounded-lg border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold-400" data-testid={testId} />
    </div>
  );
}
function Label({ children }) {
  return <div className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground mb-2">{children}</div>;
}
